import React, { useEffect, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import productoService, { type Producto } from '../../services/productoService';
import type { CotizacionPdfPrecargaItem } from '../../services/cotizacionVentaService';
import {
  esDiscontinuadoBloqueadoParaAgregar,
  filtrarProductosInactivos,
  mensajeArticuloDiscontinuadoBloqueado,
  paramsBusquedaProductoDocumento,
  type ModoBloqueoArtDiscontinuados,
} from '../../utils/bloqueaArtDiscontinuados';
import { formatCurrency } from '../../utils/formatters';

interface ProductoOpcion {
  codigoArticulo: string;
  descripcion: string;
  tipoArticulo?: string | null;
}

export interface CorreccionArticuloGenerico {
  indice: number;
  codigoArticulo: string;
  descripcion: string;
}

interface CorregirArticulosGenericosPdfDialogProps {
  open: boolean;
  items: CotizacionPdfPrecargaItem[];
  archivo?: string;
  textoCerrar?: string;
  onClose: () => void;
  onConfirm: (correcciones: CorreccionArticuloGenerico[]) => void;
}

const CorregirArticulosGenericosPdfDialog: React.FC<CorregirArticulosGenericosPdfDialogProps> = ({
  open,
  items,
  archivo,
  textoCerrar = 'Volver',
  onClose,
  onConfirm,
}) => {
  const [elegidos, setElegidos] = useState<Record<number, ProductoOpcion | null>>({});
  const [modoBloqueo, setModoBloqueo] = useState<ModoBloqueoArtDiscontinuados>(1);

  useEffect(() => {
    if (!open) return;
    setElegidos({});
    void paramsBusquedaProductoDocumento('VENTAS').then((params) => {
      setModoBloqueo(params.modoBloqueoArtDiscontinuados ?? 1);
    });
  }, [open]);

  const pendientes = items.reduce((acc, _, idx) => acc + (elegidos[idx] ? 0 : 1), 0);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>Artículos no encontrados en el maestro</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ pt: 0.5 }}>
          <Alert severity="warning" variant="outlined">
            {archivo ? `PDF: ${archivo}. ` : ''}
            {items.length === 1
              ? 'Este renglón del PDF no coincidió con un artículo y se cargaría con el producto genérico. Elegí el producto correcto antes de cargar la cotización.'
              : `Hay ${items.length} renglones del PDF que no coincidieron con un artículo y se cargarían con el producto genérico. Elegí el producto correcto antes de cargar la cotización.`}
          </Alert>

          {items.map((item, idx) => (
            <FilaCorreccion
              key={`${item.renglonPdf ?? item.descripcionOriginal ?? 'item'}-${idx}`}
              item={item}
              modoBloqueo={modoBloqueo}
              elegido={elegidos[idx] ?? null}
              onElegir={(producto) => setElegidos((prev) => ({ ...prev, [idx]: producto }))}
            />
          ))}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{textoCerrar}</Button>
        <Button
          variant="contained"
          onClick={() => {
            const correcciones: CorreccionArticuloGenerico[] = [];
            items.forEach((_, idx) => {
              const elegido = elegidos[idx];
              if (!elegido) return;
              correcciones.push({
                indice: idx,
                codigoArticulo: elegido.codigoArticulo,
                descripcion: elegido.descripcion,
              });
            });
            onConfirm(correcciones);
          }}
        >
          {pendientes > 0 ? 'Cargar con genérico' : 'Cargar cotización'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const FilaCorreccion: React.FC<{
  item: CotizacionPdfPrecargaItem;
  modoBloqueo: ModoBloqueoArtDiscontinuados;
  elegido: ProductoOpcion | null;
  onElegir: (producto: ProductoOpcion | null) => void;
}> = ({ item, modoBloqueo, elegido, onElegir }) => {
  const textoPdf = (item.descripcionOriginal || item.descripcion || '').trim();
  const renglon = (item.renglonPdf || textoPdf).trim();
  const [input, setInput] = useState(textoPdf);
  const [opciones, setOpciones] = useState<ProductoOpcion[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [aviso, setAviso] = useState<string | null>(null);

  useEffect(() => {
    const termino = input.trim();
    if (termino.length < 2) {
      setOpciones([]);
      return undefined;
    }
    let cancelado = false;
    const timer = window.setTimeout(() => {
      void (async () => {
        setBuscando(true);
        try {
          const params = await paramsBusquedaProductoDocumento('VENTAS');
          const res = await productoService.getProductos({
            searchTerm: termino,
            pageNumber: 1,
            pageSize: 8,
            ...params,
          });
          if (cancelado) return;
          const items = filtrarProductosInactivos((res.items ?? []) as Producto[]);
          setOpciones(items.map((p) => ({
            codigoArticulo: String(p.codigoArticulo ?? '').trim(),
            descripcion: String(p.descripcion ?? '').trim(),
            tipoArticulo: p.tipoArticulo ?? null,
          })).filter((p) => p.codigoArticulo));
        } catch {
          if (!cancelado) setOpciones([]);
        } finally {
          if (!cancelado) setBuscando(false);
        }
      })();
    }, 300);
    return () => {
      cancelado = true;
      window.clearTimeout(timer);
    };
  }, [input]);

  const total = item.importeTotalConIva ?? item.precioUnitario * item.cantidad;

  return (
    <Box sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1.5 }}>
      <Typography variant="caption" color="text.secondary">
        Renglón leído del PDF
      </Typography>
      <Typography
        variant="body2"
        sx={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace', whiteSpace: 'pre-wrap', mb: 1 }}
      >
        {renglon || '—'}
      </Typography>
      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
        {`Cant. ${item.cantidad} · Total ${formatCurrency(total)} · Código actual ${item.codigoArticulo || 'genérico'}`}
      </Typography>
      <Autocomplete
        options={opciones}
        value={elegido}
        inputValue={input}
        loading={buscando}
        filterOptions={(opts) => opts}
        getOptionLabel={(op) => `${op.codigoArticulo} — ${op.descripcion}`}
        isOptionEqualToValue={(a, b) => a.codigoArticulo === b.codigoArticulo}
        onInputChange={(_, value, reason) => {
          if (reason === 'reset') return;
          setInput(value);
          setAviso(null);
        }}
        onChange={(_, value) => {
          if (value && esDiscontinuadoBloqueadoParaAgregar(value.tipoArticulo, modoBloqueo)) {
            setAviso(mensajeArticuloDiscontinuadoBloqueado(value.codigoArticulo));
            return;
          }
          setAviso(null);
          onElegir(value);
          if (value) setInput(`${value.codigoArticulo} — ${value.descripcion}`);
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            label="Producto del maestro"
            size="small"
            placeholder="Buscar por código o nombre"
            InputProps={{
              ...params.InputProps,
              endAdornment: (
                <>
                  {buscando ? <CircularProgress color="inherit" size={16} /> : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            }}
          />
        )}
      />
      {aviso ? (
        <Typography variant="caption" color="warning.main" display="block" sx={{ mt: 0.5 }}>
          {aviso}
        </Typography>
      ) : null}
    </Box>
  );
};

export default CorregirArticulosGenericosPdfDialog;
